/*
 * CASA SOFIA — predictive search custom element.
 *
 * Ports app/components/SearchFormPredictive.tsx + SearchResultsPredictive.tsx +
 * the SearchAside in PageLayout.tsx to a framework-free <predictive-search>
 * custom element. It lives inside the search drawer (the aside-bus 'search'
 * overlay) and is rendered by snippets/predictive-search.liquid.
 *
 * Behaviour (mirrors the Hydrogen version):
 *   • Debounced (~200ms) fetch to Shopify's native predictive endpoint
 *       /search/suggest.json?q=…&resources[type]=product,collection,page,article,query
 *         &resources[options][unavailable_products]=last&resources[limit]=6
 *     (the JSON resources response — no section render needed).
 *   • Renders a dropdown of products (image + price), collections, pages,
 *     articles and suggested queries, matching the base.css
 *     .predictive-search-result* markup ported from SearchResultsPredictive.tsx.
 *   • Empty input → the quiet "Suggestions" list (Eyewear, Linen, Sunglasses,
 *     Ceramics, Towels) — the SUGGESTED_TERMS from PageLayout.tsx.
 *   • No matches → the "No pieces match …" empty state.
 *   • Loading → the skeleton ghost rows that ship in the markup.
 *   • Submitting (Enter) navigates to /search?q=… and closes the drawer.
 *   • An aria-live status region announces loading / result counts.
 *
 * DOM hooks (see THEME-CONVENTIONS.md e):
 *   data-predictive-input    the <input type="search">
 *   data-predictive-results  the container the dropdown renders into
 *   data-predictive-status   the aria-live status region
 *   data-predictive-suggest  the quiet pre-typing suggestions block
 *   data-predictive-skeleton  the loading skeleton block (cloned as the template)
 * Element config attributes:
 *   data-search-endpoint   the /search route (default '/search')
 *   data-limit             per-resource limit (default 6)
 *   data-money-format      shop.money_format for price rendering
 *   data-currency          shop currency iso (fallback for Intl formatting)
 * ========================================================================= */

const DEBOUNCE_MS = 200;
const SUGGEST_PATH = '/search/suggest.json';

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.controller = null;
    this.debounceId = 0;
    this.term = '';
    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onAsideOpened = this._onAsideOpened.bind(this);
    this._onAsideClosed = this._onAsideClosed.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-predictive-input]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.suggest = this.querySelector('[data-predictive-suggest]');
    this.form = this.querySelector('form') || this.closest('form');

    this.endpoint = this.getAttribute('data-search-endpoint') || '/search';
    this.limit = Number(this.getAttribute('data-limit')) || 6;
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.currency = this.getAttribute('data-currency') || 'EUR';

    // Cache the shipped skeleton HTML so we can re-show it while fetching. It is
    // parked in a <template> so it never renders on first paint; read its
    // content rather than the inert template element itself.
    const skelTpl = this.querySelector('[data-predictive-skeleton-template]');
    this.skeletonHtml = skelTpl ? skelTpl.innerHTML : '';

    if (this.input) {
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('focus', this._onFocus);
      this.input.addEventListener('keydown', this._onKeydown);
      // SearchFormPredictive forces type="search" so the field is selectable.
      this.input.setAttribute('type', 'search');
    }
    if (this.form) this.form.addEventListener('submit', this._onSubmit);

    // Reset/refocus in step with the aside bus (PageLayout closeSearch()).
    document.addEventListener('aside:opened', this._onAsideOpened);
    document.addEventListener('aside:closed', this._onAsideClosed);

    this._showSuggestions();
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('focus', this._onFocus);
      this.input.removeEventListener('keydown', this._onKeydown);
    }
    if (this.form) this.form.removeEventListener('submit', this._onSubmit);
    document.removeEventListener('aside:opened', this._onAsideOpened);
    document.removeEventListener('aside:closed', this._onAsideClosed);
    if (this.controller) this.controller.abort();
    window.clearTimeout(this.debounceId);
  }

  /* ── event handlers ─────────────────────────────────────────────────────── */
  _onInput(e) {
    this._schedule(e.target.value || '');
  }

  _onFocus(e) {
    // Mirrors onFocus={fetchResults}: re-run for the current value.
    this._schedule(e.target.value || '');
  }

  _onKeydown(e) {
    // Enter is owned by the form's submit handler (_onSubmit → _goToSearch): the
    // implicit single-input submit fires it. Escape is handled globally by the
    // aside controller (closes the drawer). Nothing to do here.
  }

  _onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    this._goToSearch();
  }

  _onAsideOpened(e) {
    if (e?.detail?.type !== 'search') return;
    // Focus the field and refresh the suggestions/results when the drawer opens.
    window.requestAnimationFrame(() => {
      if (this.input) {
        try {
          this.input.focus({preventScroll: true});
        } catch (_) {
          /* no-op */
        }
        this._schedule(this.input.value || '');
      }
    });
  }

  _onAsideClosed(e) {
    if (e?.detail?.type !== 'search') return;
    // closeSearch() in SearchResultsPredictive resets the input on close.
    if (this.input) {
      this.input.value = '';
      this.input.blur();
    }
    this.term = '';
    window.clearTimeout(this.debounceId);
    if (this.controller) this.controller.abort();
    this._showSuggestions();
  }

  /* ── fetching ───────────────────────────────────────────────────────────── */
  _schedule(value) {
    const term = value.trim();
    this.term = term;
    window.clearTimeout(this.debounceId);

    if (!term) {
      if (this.controller) this.controller.abort();
      this._showSuggestions();
      return;
    }

    this.debounceId = window.setTimeout(() => this._fetch(term), DEBOUNCE_MS);
  }

  async _fetch(term) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this._showSkeleton(term);

    const params = new URLSearchParams();
    params.set('q', term);
    params.append('resources[type]', 'product,collection,page,article,query');
    params.append('resources[options][unavailable_products]', 'last');
    params.append('resources[limit]', String(this.limit));
    params.append('resources[limit_scope]', 'each');

    const url = `${SUGGEST_PATH}?${params.toString()}`;

    try {
      const res = await fetch(url, {
        signal: this.controller.signal,
        headers: {Accept: 'application/json'},
      });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);
      const data = await res.json();
      // Guard against a stale response when the term changed mid-flight.
      if (term !== this.term) return;
      this._render(term, data?.resources?.results || {});
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (term !== this.term) return;
      // Graceful empty state, mirroring the loader's error fallback.
      this._renderEmpty(term);
    }
  }

  /* ── rendering ──────────────────────────────────────────────────────────── */
  _showSuggestions() {
    if (this.suggest) this.suggest.hidden = false;
    if (this.results) this.results.innerHTML = '';
    this._setStatus('');
  }

  _showSkeleton(term) {
    if (this.suggest) this.suggest.hidden = true;
    if (this.results && this.skeletonHtml) {
      this.results.innerHTML = this.skeletonHtml;
    }
    this._setStatus(this._t('searching', 'Searching…'));
  }

  _renderEmpty(term) {
    if (this.suggest) this.suggest.hidden = true;
    const q = escapeHtml(term);
    if (this.results) {
      this.results.innerHTML =
        `<p class="predictive-search__empty">` +
        `No pieces match <q>${q}</q>. Try another word, or browse the collections.` +
        `</p>`;
    }
    this._setStatus(this._t('no_results', 'No results found.'));
  }

  _render(term, results) {
    if (this.suggest) this.suggest.hidden = true;

    const products = results.products || [];
    const collections = results.collections || [];
    const pages = results.pages || [];
    const articles = results.articles || [];

    // Empty decision is based on visible result groups (products/collections/
    // pages/articles); query suggestions feed the input only, as in the Hydrogen
    // <datalist>, and never on their own count as "results".
    const blocks = [];
    if (products.length) blocks.push(this._productsBlock(products));
    if (collections.length) blocks.push(this._collectionsBlock(collections));
    if (pages.length) blocks.push(this._pagesBlock(pages));
    if (articles.length) blocks.push(this._articlesBlock(articles));

    if (!blocks.length) {
      this._renderEmpty(term);
      return;
    }

    const total =
      products.length + collections.length + pages.length + articles.length;

    const viewAll =
      `<a class="predictive-search__viewall" href="${this._searchUrl(term)}">` +
      `View all results for <q>${escapeHtml(term)}</q>` +
      `<span aria-hidden="true"> →</span></a>`;

    if (this.results) {
      this.results.innerHTML = blocks.join('') + viewAll;
    }
    this._setStatus(
      this._t('results_count', '%COUNT% results').replace(
        '%COUNT%',
        String(total),
      ),
    );
  }

  _heading(text) {
    return `<h5>${escapeHtml(text)}</h5>`;
  }

  _thumb(image, alt) {
    // suggest.json returns image as a CDN string URL; be tolerant of an object
    // ({url}/{src}) too in case the response shape is the GraphQL-style one.
    let url = '';
    if (typeof image === 'string') url = image;
    else if (image) url = image.url || image.src || '';
    if (!url) return '';
    // Request a small crop from the Shopify CDN for the 3rem thumbnail.
    const src = this._sized(url, 100);
    const altText = typeof image === 'object' && image ? image.alt || alt : alt;
    return `<img src="${escapeHtml(src)}" alt="${escapeHtml(altText || '')}" width="50" height="50" loading="lazy" decoding="async">`;
  }

  _sized(url, size) {
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(size));
      return u.toString();
    } catch (_) {
      return url;
    }
  }

  _productsBlock(products) {
    const items = products
      .map((p) => {
        const url = this._withTracking(p.url || `/products/${p.handle}`, p);
        const image = p.image || p.featured_image || '';
        const price = this._price(p);
        return (
          `<li class="predictive-search-result-item">` +
          `<a href="${escapeHtml(url)}">` +
          this._thumb(image, p.title) +
          `<div class="predictive-search-result-meta">` +
          `<p>${escapeHtml(p.title)}</p>` +
          (price ? `<small>${price}</small>` : '') +
          `</div></a></li>`
        );
      })
      .join('');
    return (
      `<div class="predictive-search-result">` +
      this._heading(this._t('products', 'Products')) +
      `<ul>${items}</ul></div>`
    );
  }

  _collectionsBlock(collections) {
    const items = collections
      .map((c) => {
        const url = this._withTracking(c.url || `/collections/${c.handle}`, c);
        const image = c.image || c.featured_image || '';
        return (
          `<li class="predictive-search-result-item">` +
          `<a href="${escapeHtml(url)}">` +
          this._thumb(image, c.title) +
          `<div class="predictive-search-result-meta">` +
          `<span>${escapeHtml(c.title)}</span>` +
          `</div></a></li>`
        );
      })
      .join('');
    return (
      `<div class="predictive-search-result">` +
      this._heading(this._t('collections', 'Collections')) +
      `<ul>${items}</ul></div>`
    );
  }

  _pagesBlock(pages) {
    const items = pages
      .map((pg) => {
        const url = this._withTracking(pg.url || `/pages/${pg.handle}`, pg);
        return (
          `<li class="predictive-search-result-item">` +
          `<a href="${escapeHtml(url)}">` +
          `<div class="predictive-search-result-meta">` +
          `<span>${escapeHtml(pg.title)}</span>` +
          `</div></a></li>`
        );
      })
      .join('');
    return (
      `<div class="predictive-search-result">` +
      this._heading(this._t('pages', 'Pages')) +
      `<ul>${items}</ul></div>`
    );
  }

  _articlesBlock(articles) {
    const items = articles
      .map((a) => {
        const url = this._withTracking(a.url || `/blogs/${a.handle}`, a);
        const image = a.image || a.featured_image || '';
        return (
          `<li class="predictive-search-result-item">` +
          `<a href="${escapeHtml(url)}">` +
          this._thumb(image, a.title) +
          `<div class="predictive-search-result-meta">` +
          `<span>${escapeHtml(a.title)}</span>` +
          `</div></a></li>`
        );
      })
      .join('');
    return (
      `<div class="predictive-search-result">` +
      this._heading(this._t('articles', 'Articles')) +
      `<ul>${items}</ul></div>`
    );
  }

  /* ── price + url helpers ────────────────────────────────────────────────── */
  _price(product) {
    // Shopify's /search/suggest.json returns `price` already formatted in the
    // shop currency (e.g. "€120,00"). Use it verbatim when it carries a currency
    // glyph/separator; otherwise treat it as a raw number and format it.
    const raw = product.price != null ? product.price : product.price_min;
    if (raw == null || raw === '') return '';
    const str = String(raw);
    if (/[^\d.,\s]/.test(str)) {
      // Already formatted (contains a currency symbol / non-numeric glyph).
      return escapeHtml(str);
    }
    return escapeHtml(this._formatMoney(str));
  }

  _formatMoney(value) {
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.currency,
      }).format(num);
    } catch (_) {
      return num.toFixed(2);
    }
  }

  _withTracking(baseUrl, item) {
    // urlWithTrackingParams: append the search term as ?q= for context.
    if (!this.term) return baseUrl;
    const sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    return `${baseUrl}${sep}q=${encodeURIComponent(this.term)}`;
  }

  _searchUrl(term) {
    return `${this.endpoint}?q=${encodeURIComponent(term)}`;
  }

  _goToSearch() {
    const term = this.input ? this.input.value.trim() : this.term;
    const url = term ? this._searchUrl(term) : this.endpoint;
    document.dispatchEvent(new CustomEvent('aside:close'));
    window.location.href = url;
  }

  /* ── status + i18n ──────────────────────────────────────────────────────── */
  _setStatus(text) {
    if (this.status) this.status.textContent = text;
  }

  _t(key, fallback) {
    // Optional locale strings injected as data-t-<key> attributes by the snippet.
    const attr = this.getAttribute(`data-t-${key.replace(/_/g, '-')}`);
    return attr != null && attr !== '' ? attr : fallback;
  }
}

if (
  typeof customElements !== 'undefined' &&
  !customElements.get('predictive-search')
) {
  customElements.define('predictive-search', PredictiveSearch);
}
